import React, { useState, useEffect } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';
import {
    Form,
    Label,
    Input,
    DescriptionRow,
    DescriptionInput,
    Button,
    AddMoreButton,
    RemoveFieldButton,
    Select,
    DisabledButton,
} from '../../styles';

const categoryToId = {
    SURVOL: 1,
    HISTORIQUE: 2,
    CONFIGURATIONS: 3,
    NOTIFICATIONS: 4,
    'GÉO-BARRIÈRES': 5,
    RAPPORTS: 6,
    'TABLEAU DE BORD': 7,
    ADMINISTRATION: 8,
    ENTRETIEN: 9,
    DOCUMENTATION: 10,
};

const typeToId = {
    FONCTIONNALITÉ: 1,
    CORRECTION: 2,
};

const categoriesList = [
    'SURVOL',
    'HISTORIQUE',
    'CONFIGURATIONS',
    'NOTIFICATIONS',
    'GÉO-BARRIÈRES',
    'RAPPORTS',
    'TABLEAU DE BORD',
    'ADMINISTRATION',
    'ENTRETIEN',
    'DOCUMENTATION',
];
const typesList = ['FONCTIONNALITÉ', 'CORRECTION'];

const NewsFormSubmit = ({ onSubmit, editingItem, onCancelEdit, isLoading }) => {
    const [date, setDate] = useState('');
    const [category, setCategory] = useState('');
    const [type, setType] = useState('');
    const [descriptionFR, setDescriptionFR] = useState('');
    const [descriptionEN, setDescriptionEN] = useState('');
    const [descriptionES, setDescriptionES] = useState('');
    const [additionalFields, setAdditionalFields] = useState([]);
    const [isFormValid, setIsFormValid] = useState(false);
    const [formData, setFormData] = useState({
        author: '',
        publish_date: '',
        news: [],
    });

    useEffect(
        () => {
            if (editingItem) {
                const mainNewsItem = editingItem.news[0];

                const categoryName = Object.keys(categoryToId).find(
                    (key) => categoryToId[key] === mainNewsItem.category_id
                );

                const typeName = Object.keys(typeToId).find(
                    (key) => typeToId[key] === mainNewsItem.news_type_id
                );

                setDate(editingItem.publish_date.split('T')[0]);
                setCategory(categoryName || '');
                setType(typeName || '');
                setDescriptionFR(mainNewsItem.content_fr);
                setDescriptionEN(mainNewsItem.content_en);
                setDescriptionES(mainNewsItem.content_es);

                if (editingItem.news.length > 1) {
                    const additional = editingItem.news.slice(1).map((item, index) => ({
                        id: index,
                        category:
                            Object.keys(categoryToId).find(
                                (key) => categoryToId[key] === item.category_id
                            ) || '',
                        type:
                            Object.keys(typeToId).find(
                                (key) => typeToId[key] === item.news_type_id
                            ) || '',
                        descriptionFR: item.content_fr,
                        descriptionEN: item.content_en,
                        descriptionES: item.content_es,
                    }));
                    setAdditionalFields(additional);
                } else {
                    setAdditionalFields([]);
                }

                setFormData(editingItem);
            } else {
                resetForm();
            }
        },
        [editingItem]
    );

    useEffect(
        () => {
            const isMainFormValid =
                category && type && descriptionFR && descriptionEN && descriptionES;

            const areAdditionalFieldsValid = additionalFields.every(
                (field) =>
                    field.category &&
                    field.type &&
                    field.descriptionFR &&
                    field.descriptionEN &&
                    field.descriptionES
            );

            setIsFormValid(
                isMainFormValid && (additionalFields.length === 0 || areAdditionalFieldsValid)
            );
        },
        [category, type, descriptionFR, descriptionEN, descriptionES, additionalFields]
    );

    const handleSubmit = () => {
        if (!isFormValid) return;

        const selectedDate = date || new Date().toISOString().split('T')[0];

        const mainEntry = {
            news_type_id: typeToId[type],
            category_id: categoryToId[category],
            news_image: '',
            content_fr: descriptionFR,
            content_en: descriptionEN,
            content_es: descriptionES,
        };

        const additionalEntries = additionalFields.map((field) => ({
            news_type_id: typeToId[field.type],
            category_id: categoryToId[field.category],
            news_image: '',
            content_fr: field.descriptionFR,
            content_en: field.descriptionEN,
            content_es: field.descriptionES,
        }));

        const requestBody = {
            author: '',
            publish_date: new Date(selectedDate).toISOString(),
            news: [mainEntry, ...additionalEntries],
        };

        onSubmit(requestBody, editingItem ? editingItem.articleId : undefined);

        resetForm();
    };

    const resetForm = () => {
        setDate('');
        setCategory('');
        setType('');
        setDescriptionFR('');
        setDescriptionEN('');
        setDescriptionES('');
        setAdditionalFields([]);
    };

    const addMoreFields = () => {
        setAdditionalFields([
            ...additionalFields,
            {
                id: Date.now(),
                category: '',
                type: '',
                descriptionFR: '',
                descriptionEN: '',
                descriptionES: '',
            },
        ]);
    };

    const removeField = (id) => {
        setAdditionalFields(additionalFields.filter((field) => field.id !== id));
    };

    const handleAdditionalFieldChange = (id, field, value) => {
        setAdditionalFields(
            additionalFields.map((item) => (item.id === id ? { ...item, [field]: value } : item))
        );
    };

    return (
        <Form>
            <Label>Date (optionnel - aujourd'hui par défaut)</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />

            <Label>Catégorie *</Label>
            <Select value={category} onChange={(e) => setCategory(e.target.value)} required>
                <option value="">Sélectionner une catégorie</option>
                {categoriesList.map((cat, index) => (
                    <option key={index} value={cat}>
                        {cat}
                    </option>
                ))}
            </Select>

            <Label>Type *</Label>
            <Select value={type} onChange={(e) => setType(e.target.value)} required>
                <option value="">Sélectionner un type</option>
                {typesList.map((t, index) => (
                    <option key={index} value={t}>
                        {t}
                    </option>
                ))}
            </Select>

            <Label>Descriptions *</Label>
            <DescriptionRow>
                <div>
                    <Label>Français *</Label>
                    <DescriptionInput
                        value={descriptionFR}
                        onChange={(e) => setDescriptionFR(e.target.value)}
                        required
                    />
                </div>
                <div>
                    <Label>Anglais *</Label>
                    <DescriptionInput
                        value={descriptionEN}
                        onChange={(e) => setDescriptionEN(e.target.value)}
                        required
                    />
                </div>
                <div>
                    <Label>Espagnol *</Label>
                    <DescriptionInput
                        value={descriptionES}
                        onChange={(e) => setDescriptionES(e.target.value)}
                        required
                    />
                </div>
            </DescriptionRow>

            {additionalFields.map((field) => (
                <div key={field.id} style={{ marginTop: '15px', position: 'relative' }}>
                    <RemoveFieldButton onClick={() => removeField(field.id)}>
                        <FaTimes />
                    </RemoveFieldButton>

                    <Label>Catégorie supplémentaire *</Label>
                    <Select
                        value={field.category}
                        onChange={(e) =>
                            handleAdditionalFieldChange(field.id, 'category', e.target.value)
                        }
                        required
                    >
                        <option value="">Sélectionner une catégorie</option>
                        {categoriesList.map((cat, idx) => (
                            <option key={idx} value={cat}>
                                {cat}
                            </option>
                        ))}
                    </Select>

                    <Label>Type supplémentaire *</Label>
                    <Select
                        value={field.type}
                        onChange={(e) =>
                            handleAdditionalFieldChange(field.id, 'type', e.target.value)
                        }
                        required
                    >
                        <option value="">Sélectionner un type</option>
                        {typesList.map((t, idx) => (
                            <option key={idx} value={t}>
                                {t}
                            </option>
                        ))}
                    </Select>

                    <DescriptionRow>
                        <div>
                            <Label>Français *</Label>
                            <DescriptionInput
                                value={field.descriptionFR}
                                onChange={(e) =>
                                    handleAdditionalFieldChange(
                                        field.id,
                                        'descriptionFR',
                                        e.target.value
                                    )
                                }
                                required
                            />
                        </div>
                        <div>
                            <Label>Anglais *</Label>
                            <DescriptionInput
                                value={field.descriptionEN}
                                onChange={(e) =>
                                    handleAdditionalFieldChange(
                                        field.id,
                                        'descriptionEN',
                                        e.target.value
                                    )
                                }
                                required
                            />
                        </div>
                        <div>
                            <Label>Espagnol *</Label>
                            <DescriptionInput
                                value={field.descriptionES}
                                onChange={(e) =>
                                    handleAdditionalFieldChange(
                                        field.id,
                                        'descriptionES',
                                        e.target.value
                                    )
                                }
                                required
                            />
                        </div>
                    </DescriptionRow>
                </div>
            ))}

            {!editingItem && (
                <AddMoreButton onClick={addMoreFields}>
                    <FaPlus /> Ajouter plus de descriptions
                </AddMoreButton>
            )}

            {isFormValid ? (
                <Button onClick={handleSubmit} disabled={isLoading}>
                    {editingItem ? 'Mettre à jour' : 'Ajouter'}
                </Button>
            ) : (
                <DisabledButton disabled>
                    {editingItem ? 'Mettre à jour' : 'Ajouter'}
                </DisabledButton>
            )}

            {editingItem && (
                <Button onClick={onCancelEdit} style={{ marginLeft: '10px', background: '#ccc' }}>
                    Annuler
                </Button>
            )}
        </Form>
    );
};

export default NewsFormSubmit;
